import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI } from '../../services/api';
import { Loader2, FileText, Upload, ArrowRightLeft } from 'lucide-react';

// P3B — Reasignación auditada y expediente del prospecto. Reutiliza el CRM y el
// almacenamiento privado persistente existentes; no crea pantallas paralelas.
export const ProspectoAcciones = ({ prospecto, asesores, onCerrar, onActualizado }) => {
  const { toast } = useToast();
  const [destino, setDestino] = useState('');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [docs, setDocs] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [archivo, setArchivo] = useState(null);

  const recargar = () => {
    crmTerritorialAPI.documentosProspecto(prospecto.oportunidad_id)
      .then((d) => setDocs(d.items || [])).catch(() => setDocs([]));
    crmTerritorialAPI.historialProspecto(prospecto.oportunidad_id)
      .then((d) => setHistorial(d.eventos || [])).catch(() => setHistorial([]));
  };
  useEffect(recargar, [prospecto.oportunidad_id]);

  const mismaSeccion = asesores.filter(
    (a) => a.seccion_id === prospecto.seccion_id && a.asesor_id !== prospecto.asesor?.id);

  const reasignar = async () => {
    setEnviando(true);
    try {
      const r = await crmTerritorialAPI.reasignarProspecto(prospecto.oportunidad_id,
        { asesor_destino_id: destino, motivo });
      toast({ title: 'Prospecto reasignado',
        description: `Ahora lo atiende ${r.asesor_nuevo_nombre}. Actividades trasladadas: ${r.actividades_pendientes_trasladadas}.` });
      onActualizado();
      onCerrar();
    } catch (e) {
      toast({ title: 'Reasignación rechazada', description: e.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  const subir = async () => {
    if (!archivo) return;
    setEnviando(true);
    try {
      await crmTerritorialAPI.subirDocumentoProspecto(prospecto.oportunidad_id, archivo, 'otro');
      toast({ title: 'Documento agregado al expediente' });
      setArchivo(null);
      recargar();
    } catch (e) {
      toast({ title: 'No se pudo subir', description: e.message, variant: 'destructive' });
    } finally { setEnviando(false); }
  };

  return (
    <Dialog open onOpenChange={onCerrar}>
      <DialogContent className="max-w-2xl" data-testid="prospecto-acciones-dialog">
        <DialogHeader>
          <DialogTitle>{prospecto.nombre}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <section className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center">
              <ArrowRightLeft className="h-4 w-4 mr-2 text-gray-400" />
              Reasignar dentro de {prospecto.seccion}
            </h3>
            <select value={destino} onChange={(e) => setDestino(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                    data-testid="prospecto-reasignar-destino">
              <option value="">Elige al asesor que lo atenderá</option>
              {mismaSeccion.map((a) => (
                <option key={a.asesor_id} value={a.asesor_id}>
                  {a.nombre} · {a.prospectos} prospecto(s)
                </option>
              ))}
            </select>
            <Input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                   placeholder="Motivo de la reasignación (queda en la auditoría)"
                   data-testid="prospecto-reasignar-motivo" />
            <Button onClick={reasignar} disabled={!destino || motivo.length < 4 || enviando}
                    data-testid="prospecto-reasignar-btn">
              {enviando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reasignar prospecto
            </Button>
            <p className="text-xs text-gray-500">
              Sólo asesores de la misma sección. El traslado se registra con actor, motivo y fecha.
            </p>
          </section>

          <section className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center">
              <FileText className="h-4 w-4 mr-2 text-gray-400" />
              Expediente ({docs.length})
            </h3>
            <ul className="text-sm space-y-1" data-testid="prospecto-expediente-lista">
              {docs.length === 0 && <li className="text-gray-500">Sin documentos todavía.</li>}
              {docs.map((d) => (
                <li key={d.documento_id} className="flex items-center justify-between border-b py-1">
                  <span>{d.nombre} <span className="text-gray-400">· {d.tipo}</span></span>
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    crmTerritorialAPI.descargarDocumento(d.documento_id);
                  }} className="text-blue-600 hover:underline"
                     data-testid={`prospecto-doc-descargar-${d.documento_id}`}>Descargar</a>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-2">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,.docx"
                     onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                     className="text-sm" data-testid="prospecto-doc-input" />
              <Button size="sm" variant="outline" onClick={subir} disabled={!archivo || enviando}
                      data-testid="prospecto-doc-subir-btn">
                <Upload className="h-4 w-4 mr-2" />Subir
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Almacenamiento privado y persistente. Máximo 5 MB por archivo (PDF, imagen o DOCX).
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Historial de reasignaciones</h3>
            <ul className="text-xs space-y-1" data-testid="prospecto-historial">
              {historial.length === 0 && <li className="text-gray-500">Sin movimientos.</li>}
              {historial.map((e, i) => (
                <li key={i} className={e.resultado === 'denied' ? 'text-red-600' : 'text-gray-700'}>
                  {new Date(e.fecha).toLocaleString('es-MX')} · {e.actor} ·{' '}
                  {e.asesor_anterior_nombre || '—'} → {e.asesor_nuevo_nombre || '—'} · {e.motivo}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProspectoAcciones;
